<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title v-bind:title="'Herd Profile'"
      v-bind:icon="'multiline_chart'"
    />

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <template v-if="exists">
      <template v-if="!isLoading && !isError" >
        <v-card :color="appColor_PanelBackgroundLight">

          <!-- Header Toolbar -->
          <v-toolbar>
            <v-toolbar-title :class="isSmallDevice ? 'subtitle-1' : 'headline'">{{ reportTitle }}</v-toolbar-title>
            <v-spacer />
            <v-btn :small="isSmallDevice" outlined @click="editParams" :color="appColor_ContentToolbar">
              <v-icon :small="isSmallDevice" :color="appColor_Selected">settings</v-icon>
            </v-btn>
          </v-toolbar>

          <v-row no-gutters class="pa-3">

            <!-- Herd Summary -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Herd Summary
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">
                  <v-simple-table height="250">
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td><span :class="labelClass">Calves Born:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.numberCalvesBorn }}</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Calves Weaned:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.numberCalvesWeaned }}</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Wean Weight:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.averageWeanWeight }} lbs</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Cow Age:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.averageCowAge }} lbs</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Calf Wean Age:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.averageCalfWeanAgeDays }} lbs</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Total Lbs Weaned:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.totalLbsWeaned }} lbs</span></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Estrus Cycles -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Estrus Cycles
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">
                  <bar-chart
                    style="height: 250px"
                    :chart-data="estrusData"
                    :options="estrusOptions"
                  />
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Calving Interval -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Calving Interval
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">
                  <v-simple-table height="250">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th />
                          <th class="text-left"><span :class="labelClass">Heifers</span></th>
                          <th class="text-left"><span :class="labelClass">Cows</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><span :class="labelClass">First Born:</span></td>
                          <td><span :class="valueClass">{{ getDateForDisplay(herdProfile.heifersCalvingInterval.firstCalfBorn) }}</span></td>
                          <td><span :class="valueClass">{{ getDateForDisplay(herdProfile.cowsCalvingInterval.firstCalfBorn) }}</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Last Born:</span></td>
                          <td><span :class="valueClass">{{ getDateForDisplay(herdProfile.heifersCalvingInterval.lastCalfBorn) }}</span></td>
                          <td><span :class="valueClass">{{ getDateForDisplay(herdProfile.cowsCalvingInterval.lastCalfBorn) }}</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Interval:</span></td>
                          <td>
                            <template v-if="herdProfile.heifersCalvingInterval.numberDays !== null">
                              <span :class="valueClass">{{ herdProfile.heifersCalvingInterval.numberDays }} days</span>
                            </template>
                          </td>
                          <td>
                            <template v-if="herdProfile.cowsCalvingInterval.numberDays !== null">
                              <span :class="valueClass">{{ herdProfile.cowsCalvingInterval.numberDays }} days</span>
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Weaning Summary -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Weaning Summary
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">
                  <v-simple-table height="250">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th />
                          <th class="text-left"><span :class="labelClass">Wean Wt</span></th>
                          <th class="text-left"><span :class="labelClass">180 Day Wt</span></th>
                          <th class="text-left"><span :class="labelClass">ADG B-W</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><span :class="labelClass">Males:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.malesWeaningSummary.weanWeight }} lbs</span></td>
                          <td><span :class="valueClass">{{ herdProfile.malesWeaningSummary.adj180WeanWt }} lbs</span></td>
                          <td><span :class="valueClass">{{ herdProfile.malesWeaningSummary.adgbw }} lbs</span></td>
                        </tr>
                        <tr>
                          <td><span :class="labelClass">Females:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.femalesWeaningSummary.weanWeight }} lbs</span></td>
                          <td><span :class="valueClass">{{ herdProfile.femalesWeaningSummary.adj180WeanWt }} lbs</span></td>
                          <td><span :class="valueClass">{{ herdProfile.femalesWeaningSummary.adgbw }} lbs</span></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Ratios -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Ratios
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">

                  <v-simple-table height="250">
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td><span :class="labelClass">Lbs Weaned / Exposed:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.lbsWeanedPerCowExposed }}:</span></td>
                        </tr>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row class="pl-3">
                                <span :class="labelClass">Cow / Bull:</span>
                              </v-row>
                              <v-row class="pl-3">
                                <span :class="hintClass">(# cows per bull)</span>
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ formatNumber(herdProfile.cowBullRatio) }}:</span></td>
                        </tr>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row class="pl-3">
                                <span :class="labelClass">Heifer / Bull:</span>
                              </v-row>
                              <v-row class="pl-3">
                                <span :class="hintClass">(# cows per bull) / (# heifers per bull):</span>
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ formatNumber(herdProfile.heiferBullRatio) }} lbs:</span></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- GOLD Indicators -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    <span>GOLD Indicators</span>
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass">
                  <v-simple-table height="250">
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row>
                                <span :class="labelGoldClass">G</span><span :class="labelClass">rowth (wean wt):</span>
                              </v-row>
                              <v-row>
                                <span :class="hintClass">(lbs weaned / # calves weaned)</span>                          
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ herdProfile.growthWeanWt }} lbs</span></td>
                        </tr>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row>
                                <span :class="labelGoldClass">O</span><span :class="labelClass">pen (cow %):</span>
                              </v-row>
                              <v-row>
                                <span :class="hintClass">(# open cows AND heifers / # exposed females)</span>
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ herdProfile.openCowsPct }} %</span></td>
                        </tr>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row>
                                <span :class="labelGoldClass">L</span><span :class="labelClass">ength of Calving Perdiod:</span>
                              </v-row>
                              <v-row>
                                <span :class="hintClass">(days from first to last calf)</span>
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ herdProfile.calvingPeriodLengthDays }} days</span></td>
                        </tr>
                        <tr>
                          <td>
                            <v-card-text :class="twoLineClass">
                              <v-row>
                                <span :class="labelGoldClass">D</span><span :class="labelClass">eath Loss:</span>
                              </v-row>
                              <v-row>
                                <span :class="hintClass">(# calves died / live birth)</span>
                              </v-row>
                            </v-card-text>
                          </td>
                          <td><span :class="valueClass">{{ herdProfile.calvingDeathLossPct }} %</span></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Performance Rates -->
            <v-col cols="12" xs="12" sm="6" md="3">
              <v-card :class="cardClass">
                <v-toolbar
                  :color="appColor_ContentToolbar"
                  dark
                  dense
                  flat
                  >
                  <v-toolbar-title>
                    Performance Rates
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon>
                    <v-icon color="light-blue">info</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text :class="cardTextClass" height="250">
                  <v-simple-table height="250" dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td>
                            <p :class="titleClass">Conception</p>
                          </td>
                          <td />
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Heifers:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.heiferConceptionRate }} %</span></td>
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Cows:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.cowConceptionRate }} %</span></td>
                        </tr>
                        <tr>
                          <td>
                            <p :class="titleClass">Calving</p>
                          </td>
                          <td />
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Heifers:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.heiferCalvingRate }} %</span></td>
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Cows:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.cowCalvingRate }} %</span></td>
                        </tr>
                        <tr>
                          <td>
                            <p :class="titleClass">Weaning</p>
                          </td>
                          <td />
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Heifers:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.heiferWeaningRate }} %</span></td>
                        </tr>
                        <tr>
                          <td><span :class="headingClass + ' ml-4'">Cows:</span></td>
                          <td><span :class="valueClass">{{ herdProfile.cowWeaningRate }} %</span></td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- Params Dialog -->
          <v-dialog
            v-model="paramsDialog"
            max-width="480px"
            :persistent="true"
            >
            <v-card>
              <v-toolbar flat dark :dense="isSmallDevice" :color="appColor_ContentToolbar">
                <v-btn :small="isSmallDevice" icon @click="cancel">
                  <v-icon>close</v-icon>
                </v-btn>
                <v-toolbar-title :class="isSmallDevice ? 'body-1 pl-2' : 'pl-2'">
                  {{ settingsTitle }}
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                  :small="isSmallDevice" 
                  outlined
                  @click="save"
                  :loading="isSaving"
                  :disabled="isSaving">
                  Save
                  <span slot="loader" class="custom-loader">
                    <v-icon :small="isSmallDevice">cached</v-icon>
                  </span>
                </v-btn>
              </v-toolbar>
              
              <loading
                :active.sync="isSaving"
                :can-cancel="false"
                loader="dots"
                :color="appColor_Progress"
              />

              <v-card-text>
                <v-container class="pa-1">
                  <form>
                    <v-row>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="9"
                        >
                        <v-select
                          v-model="herdName"
                          :items="herds"
                          label="Herd"
                          @change="herdNameChanged"
                          hide-details
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="3"
                        >
                        <v-select
                          v-model="year"
                          :items="years"
                          label="Year"
                          @change="yearChanged"
                          hide-details
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="12"
                        >
                        <v-select
                          v-model="herdGroup"
                          :items="groups"
                          label="Herd Group"
                          @change="herdGroupChanged"
                          hide-details
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <v-menu
                          ref="menuCowBullsOut"
                          v-model="menuCowBullsOut"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="editedParams.cowBullsOutDate"
                          transition="scale-transition"
                          offset-y
                          >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              slot="activator"
                              v-model="formattedCowBullsOutDate"
                              label="Cow Bulls Out"
                              prepend-icon="event"
                              readonly
                              clearable
                              hide-details
                              @click:clear="clearCowBullsOut"
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            v-model="editedParams.cowBullsOutDate"
                            @input="$refs.menuCowBullsOut.save(editedParams.cowBullsOutDate)"
                            hide-details
                          />
                        </v-menu>
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <v-menu
                          ref="menuHeiferBullsOut"
                          v-model="menuHeiferBullsOut"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="editedParams.heiferBullsOutDate"
                          transition="scale-transition"
                          offset-y
                          >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              v-model="formattedHeiferBullsOutDate"
                              label="Heifer Bulls Out"
                              prepend-icon="event"
                              readonly
                              clearable
                              hide-details
                              @click:clear="clearHeiferBullsOut"
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            v-model="editedParams.heiferBullsOutDate"
                            @input="$refs.menuHeiferBullsOut.save(editedParams.heiferBullsOutDate)"
                          />
                        </v-menu>
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numCowsExposed"
                          :label="'# Cows Exposed'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numHeifersExposed"
                          :label="'# Heifers Exposed'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numCowsBred"
                          :label="'# Cows Bred'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numHefersBred"
                          :label="'# Hefers Bred'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numCowBullsUsed"
                          :label="'# Cow Bulls Used'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.numHeiferBullsUsed"
                          :label="'# Cow Heifer Used'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.openCount"
                          :label="'# Open'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.diedCount"
                          :label="'# Died'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedParams.avgMatureCowWt"
                          :label="'Avg Mature Cow Wt'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>
                    </v-row>
                  </form>
                </v-container>
              </v-card-text>
            </v-card>
          </v-dialog>

          <!-- Error Dialog -->
          <v-dialog v-model="errorDialog" max-width="400px">
            <app-error
              :error-title="errorTitle"
              :error-text="errorText"
              @close="errorDialog = false"
            />
          </v-dialog>
        </v-card>
      </template>
    </template>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import BarChart from '@/charts/barChart'
import NumberField from '@/components/NumberField'
import AppError from '@/components/AppError'
import { mapGetters } from 'vuex'
import { AppColor } from '@/config.js'

export default {
  name: 'HerdProfile',
  components: {
    AppPageTitle,
    AppError,
    NumberField,
    BarChart
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_ContentToolbar: AppColor.ContentToolbar,
      appColor_PanelBackgroundLight: AppColor.PanelBackgroundLight,
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      isLoading: true,
      isSaving: false,
      isUserHerd: false,
      isUnderDevelopment: false,
      exists: true,
      paramsDialog: false,
      herdName: null,
      year: null,
      herdGroup: null,
      herds: [],
      herdGroups: [],
      editedParams: {
        id: null,
        herdName: null,
        year: null,
        herdGroup: null,
        cowBullsOutDate: null,
        heiferBullsOutDate: null,
        numCowsExposed: null,
        numHeifersExposed: null,
        numCowsBred: null,
        numHeifersBred: null,
        numCowBullsUsed: null,
        numHeiferBullsUsed: null,
        avgMatureCowWt: null,
        openCount: null,
        diedCount: null
      },
      defaultParams: {
        id: null,
        herdName: null,
        year: null,
        herdGroup: null,
        cowBullsOutDate: null,
        heiferBullsOutDate: null,
        numCowsExposed: null,
        numHeifersExposed: null,
        numCowsBred: null,
        numHeifersBred: null,
        numCowBullsUsed: null,
        numHeiferBullsUsed: null,
        avgMatureCowWt: null,
        openCount: null,
        diedCount: null
      },
      herdProfile: null,
      menuCowBullsOut: false,
      menuHeiferBullsOut: false,
      dateFormat: 'MMM DD, YYYY',
      herdYears: null,
      estrusData: null,
      estrusOptions: {
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            },
            gridLines: {
              display: true
            }
          }],
          xAxes: [{
            ticks: {
              beginAtZero: true
            },
            gridLines: {
              display: false
            }
          }]
        },
        legend: {
          display: true
        },
        tooltips: {
          enabled: true,
          mode: 'single',
          callbacks: {
            label: function (tooltipItems) {
              return tooltipItems.yLabel + ' born'
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        height: 500
      },

      maxRules: [
        ()=> {
          if (this.text1 > 9999) {
            return 'Error'
          }
        }
      ],

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    ...mapGetters(['profile']),
    cardClass () {
      return 'ma-2 elevation-5'
    },
    cardTextClass () {
      return 'pa-2'
    },
    headingClass () {
      return 'body-2 font-weight-medium'
    },
    labelClass () {
      return 'body-2 font-weight-bold'
    },
    valueClass () {
      return 'body-2 font-weight-light pr-0'
    },
    labelGoldClass () {
      return 'body-2 font-weight-bold amber--text'
    },
    titleClass () {
      return 'body-1 font-italic grey--text my-1'
    },
    hintClass () {
      return 'caption font-italic'
    },
    twoLineClass () {
      return 'pl-0 pr-0 pt-2 pb-2'
    },
    chipClass () {
      return 'elevation-2 light-blue mt-1 mb-2'
    },
    years () {
      if (this.herdYears === null) return []

      const self = this
      var herdYears = self.herdYears.filter(function (value) {
        return value.herdName === self.herdName
      })
      if (herdYears.length === 0) return []

      // assemble available years for the selected herd
      const yrs = herdYears.map(hy => {
        return {
          value: hy.year.toString(),
          text: hy.year.toString()
        }
      })

      return yrs
    },
    groups () {
      if (this.herdGroups.length === 0) return []

      const self = this
      // assemble available herd groups for the selected herd
      const herdGroups = self.herdGroups.filter(function (value) {
        return value.herdName === self.herdName
      })
      if (herdGroups.length === 0) return []

      return herdGroups[0].groups
    },
    formattedCowBullsOutDate () {
      return this.getDateForDisplay(this.editedParams.cowBullsOutDate)
    },
    formattedHeiferBullsOutDate () {
      return this.getDateForDisplay(this.editedParams.heiferBullsOutDate)
    },
    reportTitle () {
      const herdName = this.herdName
      const year = this.storedYear
      const herdGroup = this.storedHerdGroup

      if (herdName === null || year === 0) return ''

      if (herdGroup !== null && herdGroup.length > 0) {
        return herdName + ' - ' + herdGroup + ' (' + year + ')'
      } else {
        return herdName + ' (' + year + ')'
      }
    },
    storedHerdName () {
      const self = this
      if (self.profile.herdProfile.herdName !== null) {
        self.herdName = self.profile.herdProfile.herdName
      } else {
        return ''
      }
      return self.herdName
    },
    storedYear () {
      if (this.profile.herdProfile.year !== null) {
        return parseInt(this.profile.herdProfile.year)
      } else {
        return 0
      }
    },
    storedHerdGroup () {
      return this.profile.herdProfile.herdGroup
    },
    settingsTitle () {
      return this.isSmallDevice ? 'Settings' : 'Herd Profile Settings'
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      if (this.isUnderDevelopment) return

      const self = this
      appService.getHerdYears(self.profile.id).then((data) => {
        if (data.herds !== undefined && data.herds.length > 0) {
          self.isUserHerd = (data.errorSeverity === null || data.errorSeverity < 1)
          self.herdYears = data.herdYears
          self.herds = data.herds
          self.herdGroups = data.herdGroups
          if (data.herdYears.length > 0) {
            self.initHerdYear()
            // load the report
            self.loadHerdProfile()
          }
        } else {
          self.isUserHerd = false
        }
      })
    },

    loadHerdProfile () {
      const self = this
      const hy = self.editedParams
      self.isError = false
      if (hy !== null) {
        let params = {
          herdName: hy.herdName,
          year: hy.year,
          herdGroup: hy.herdGroup,
          cowBullsOutDate: hy.cowBullsOutDate,
          heiferBullsOutDate: hy.heiferBullsOutDate,
          cowsExposedCount: hy.numCowsExposed,
          heifersExposedCount: hy.numHeifersExposed,
          cowsBredCount: hy.numCowsBred,
          heifersBredCount: hy.numHeifersBred,
          cowBullsCount: hy.numCowBullsUsed,
          heiferBullsCount: hy.numHeiferBullsUsed,
          avgMatureCowWt: hy.avgMatureCowWt,
          openCount: hy.openCount,
          diedCount: hy.diedCount
        }
        self.isLoading = true
        appService.getHerdProfile(self.profile.id, params).then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorText = '<v-flex class="text-xs-left">' + data.errorMessage + '</v-flex>'
            self.errorDialog = true
          } else {
            self.herdProfile = data.herdProfile
            self.estrusData = data.estrusCyclesChart
            self.estrusData.datasets[0].backgroundColor = '#FF8A80' // red-accent-1
            self.estrusData.datasets[1].backgroundColor = '#81C784' // green-lighten-2
          }
          self.isLoading = false
        })
      }
    },

    getDateForDisplay (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('MMM DD, YYYY')
        }
      }
      return ''
    },

    getDate (date) {
      if (date !== null) {
        if (date.length > 0) {
          return this.$moment(date).format('YYYY-MM-DD')
        }
      }
      return null
    },

    setParams () {
      if (this.herdYears === null) return null
      if (this.herdYears.length === 0) return null

      const self = this
      var params = null

      // all herd years for the herd/year/herd group
      const herdHerdYears = self.herdYears.filter(function (value) {
        return value.herdName === self.herdName
      })
      if (herdHerdYears.length === 0) return []

      // all herd years for the herd
      const yearHerdYears = herdHerdYears.filter(function (value) {
        return value.year === parseInt(self.year)
      })

      // if no herd years found for the current year return the first available for the herd
      if (yearHerdYears.length === 0) {
        params = herdHerdYears[0]
      }

      if (params === null) {
        // all herd years for the herd group
        const groupHerdYears = yearHerdYears.filter(function (value) {
          return value.herdGroup === self.herdGroup
        })

        // if no herd years found for the current herd group return the first available for the herd/year
        if (groupHerdYears.length === 0) {
          params = yearHerdYears[0]
        } else {
          params = groupHerdYears[0]
        }
      }

      self.herdName = params.herdName
      self.year = params.year.toString()
      self.herdGroup = params.herdGroup

      self.editedParams = Object.assign({}, params)
      self.editedParams.cowBullsOutDate = self.getDate(params.cowBullsOutDate)
      self.editedParams.heiferBullsOutDate = self.getDate(params.heiferBullsOutDate)
    },

    editParams () {
      this.defaultParams = Object.assign({}, this.editedParams)
      this.herdName = this.defaultParams.herdName
      this.year = this.defaultParams.year.toString()
      this.herdGroup = this.defaultParams.herdGroup
      this.paramsDialog = true
    },

    cancel () {
      const self = this
      self.paramsDialog = false
      setTimeout(() => {
        self.editedParams = Object.assign({}, self.defaultParams)
        self.herdName = self.editedParams.herdName
        self.year = self.editedParams.year
        self.herdGroup = self.editedParams.herdGroup
      }, 300)
    },

    save () {
      const self = this
      self.defaultParams = Object.assign({}, self.editedParams)
      self.isSaving = true
      appService.updateHerdYears(self.editedParams).then((data) => {
        if (data.errorSeverity > 0) {
          self.paramsDialog = false
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.paramsDialog = false
          self.isSaving = false
          self.storeHerdYearGroup()
          self.loadHerdProfile()
        }
      })
    },

    storeHerdYearGroup () {
      this.$store.dispatch('updateHerdProfileSettings', {
        herdName: this.herdName,
        year: this.year.toString(),
        herdGroup: this.herdGroup
      })
    },

    initHerdYear () {
      if (this.storedHerdName.length > 0 && this.storedYear > 0) {
        this.herdName = this.storedHerdName
        this.year = this.storedYear.toString()
        this.herdGroup = this.storedHerdGroup
      } else {
        this.herdName = this.herdYears[0].herdName
        this.year = this.herdYears[0].year.toString()
        this.herdGroup = this.groups[0].value
      }
      this.storeHerdYearGroup()
      const hy = this.herdYears[0]
      this.defaultParams = Object.assign({}, hy)
      this.editedParams = Object.assign({}, hy)
      this.defaultParams.cowBullsOutDate = this.getDate(hy.cowBullsOutDate)
      this.defaultParams.heiferBullsOutDate = this.getDate(hy.heiferBullsOutDate)
      this.editedParams.cowBullsOutDate = this.getDate(hy.cowBullsOutDate)
      this.editedParams.heiferBullsOutDate = this.getDate(hy.heiferBullsOutDate)
    },

    formatNumber (x) {
      if (typeof x === 'undefined') return ''
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    clearCowBullsOut () {
      this.editedParams.cowBullsOutDate = null
    },

    clearHeiferBullsOut () {
      this.editedParams.heiferBullsOutDate = null
    },

    herdNameChanged () {
      this.setParams()
    },

    yearChanged () {
      this.setParams()
    },

    herdGroupChanged () {
      this.setParams()
    }
  }
}
</script>

